/**
 * Every route in the console, in one place.
 *
 * Pages are lazy so the first paint is the shell and the sign-in screen rather
 * than the whole application. `RequireAuth` wraps the shell layout, so no page
 * has to think about whether there is a session.
 *
 * PAGE OWNERSHIP: each `src/pages/*` file below is a reserved path. The files
 * marked "reserved" render nothing yet and are meant to be replaced wholesale by
 * the agent that owns the screen. If a screen ends up living in its own folder
 * (for example `src/pages/prospects/ProspectListPage.tsx`), point the lazy
 * import here at that module and delete the reserved file, so this stays the one
 * place a route is defined.
 */
import { Suspense, lazy } from 'react'
import { Navigate, Outlet, createBrowserRouter, useParams } from 'react-router-dom'
import { AI_ARK_PUT_AWAY } from '@gojibasel/shared/ai-ark'
import type { RouteObject } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { TakeoverLayout } from './layouts/TakeoverLayout'
import { SettingsLayout } from './pages/settings/SettingsLayout'
import { KnowledgeLayout } from './pages/knowledge/KnowledgeLayout'
import { LoadingState } from './components/States'
import { Redirect } from './lib/redirect'
import { RequireAdmin, RequireAuth } from './lib/session'
import { RequireSuperAdminView } from './components/RequireSuperAdminView'
import { ProspectingLayout } from './pages/prospecting/shared/ProspectingAccess'
import { LegacyProspectingRedirect } from './pages/prospecting/shared/LegacyProspectingRedirect'
import { OperatorLayout, RequireOperatorAdmin } from './pages/operator/shared/OperatorAccess'
import { LegacyOperatorRedirect } from './lib/legacy-operator-redirect'

// Sign-in is part of the shell, so it is not lazy: it is often the first paint.
import Login from './pages/Login'
import NotFound from './pages/NotFound'

// Reserved page files. Replace the file, not this import.
const Prospects = lazy(() => import('./pages/Prospects'))
const ProspectDetail = lazy(() => import('./pages/ProspectDetail'))
const Playbooks = lazy(() => import('./pages/playbooks/PlaybookListPage').then((m) => ({ default: m.PlaybookListPage })))
const PlaybookNew = lazy(() => import('./pages/playbooks/PlaybookWizard').then((m) => ({ default: m.PlaybookWizard })))
const PlaybookDetail = lazy(() => import('./pages/playbooks/PlaybookDetailPage').then((m) => ({ default: m.PlaybookDetailPage })))
const PlaybookBuilder = lazy(() =>
  import('./pages/playbooks/builder/PlaybookBuilderPage').then((m) => ({ default: m.PlaybookBuilderPage })),
)
const MethodDetail = lazy(() => import('./pages/MethodDetail'))
const Approvals = lazy(() => import('./pages/Approvals'))
const Queue = lazy(() => import('./pages/Queue'))
const Inbox = lazy(() => import('./pages/Inbox'))
const SequencesAnalytics = lazy(() =>
  import('./pages/playbooks/SequencesAnalyticsPage').then((m) => ({ default: m.SequencesAnalyticsPage })),
)
const SequencesWorkflows = lazy(() =>
  import('./pages/playbooks/SequencesWorkflowsPage').then((m) => ({ default: m.SequencesWorkflowsPage })),
)
const Senders = lazy(() => import('./pages/senders/SendersPage').then((m) => ({ default: m.SendersPage })))
const SendersMailboxes = lazy(() => import('./pages/senders/MailboxesPage').then((m) => ({ default: m.MailboxesPage })))
const SendersBrands = lazy(() => import('./pages/senders/BrandSendersPage').then((m) => ({ default: m.BrandSendersPage })))
const SendersPostAnalytics = lazy(() =>
  import('./pages/senders/PostAnalyticsPage').then((m) => ({ default: m.PostAnalyticsPage })),
)
const SendersPhones = lazy(() => import('./pages/senders/PhonesPage').then((m) => ({ default: m.PhonesPage })))
const ProspectLists = lazy(() =>
  import('./pages/prospects/lists/ProspectListsPage').then((m) => ({ default: m.ProspectListsPage })),
)
const SettingsSafetyControls = lazy(() => import('./pages/settings/SafetyControls'))
const SettingsBrands = lazy(() => import('./pages/settings/Brands'))
const SettingsBrandPrompts = lazy(() => import('./pages/settings/BrandPromptsPage'))
const KnowledgeOffers = lazy(() => import('./pages/settings/Offers'))
const KnowledgeBrandFile = lazy(() => import('./pages/knowledge/BrandFilePage'))
const SettingsLeadMagnets = lazy(() => import('./pages/lead-magnets/LeadMagnetsPage'))
const SettingsSuppressions = lazy(() => import('./pages/settings/Suppressions'))
const SettingsActivity = lazy(() => import('./pages/settings/ActivityLog'))
const SettingsLiveFeed = lazy(() => import('./pages/settings/LiveFeed'))
const SettingsVariables = lazy(() => import('./pages/settings/Variables'))
const Handbook = lazy(() => import('./pages/settings/Handbook'))
const ReviewNames = lazy(() => import('./pages/prospects/ReviewNamesPage'))
const ProspectImport = lazy(() =>
  import('./pages/prospects/ProspectImportPage').then((m) => ({ default: m.ProspectImportPage })),
)
const LumaEvents = lazy(() =>
  import('./pages/prospects/luma/LumaEventsPage').then((m) => ({ default: m.LumaEventsPage })),
)
const EnrichmentQueue = lazy(() =>
  import('./pages/prospects/enrichment/EnrichmentPage').then((m) => ({ default: m.EnrichmentPage })),
)
const CheckLinks = lazy(() =>
  import('./pages/prospects/enrichment/CheckLinksPage').then((m) => ({ default: m.CheckLinksPage })),
)
const ProfileSearch = lazy(() =>
  import('./pages/prospects/profile-search/ProfileSearchPage').then((m) => ({ default: m.ProfileSearchPage })),
)
const SalesNavigatorImport = lazy(() =>
  import('./pages/prospects/salesnav/SalesNavigatorImportPage').then((m) => ({
    default: m.SalesNavigatorImportPage,
  })),
)
const SettingsApi = lazy(() => import('./pages/settings/Api'))
const SettingsNotifications = lazy(() => import('./pages/settings/Notifications'))
const SettingsIntegrations = lazy(() => import('./pages/settings/Integrations'))
const SettingsAiModels = lazy(() => import('./pages/settings/AiModels'))
// Prospecting (docs/PROSPECTING-MERGE.md): the prospects pages, part of this app
// since 2026-10-24. Each folder under pages/prospecting is one page group.
const ProspectingQueue = lazy(() => import('./pages/prospecting/queue/QueuePage').then((m) => ({ default: m.QueuePage })))
const ProspectingPosts = lazy(() => import('./pages/prospecting/posts/PostsPage').then((m) => ({ default: m.PostsPage })))
const ProspectingCreators = lazy(() => import('./pages/prospecting/audience/CreatorsPage').then((m) => ({ default: m.CreatorsPage })))
const ProspectingMentions = lazy(() => import('./pages/prospecting/audience/MentionsPage').then((m) => ({ default: m.MentionsPage })))
const ProspectingCompetitors = lazy(() =>
  import('./pages/prospecting/audience/CompetitorsPage').then((m) => ({ default: m.CompetitorsPage })),
)
const ProspectingReviews = lazy(() => import('./pages/prospecting/audience/ReviewsPage').then((m) => ({ default: m.ReviewsPage })))
const ProspectingProspects = lazy(() => import('./pages/prospecting/people/ProspectsPage').then((m) => ({ default: m.ProspectsPage })))
const ProspectingSequences = lazy(() => import('./pages/prospecting/people/SequencesPage').then((m) => ({ default: m.SequencesPage })))
const ProspectingTrends = lazy(() => import('./pages/prospecting/insights/TrendsPage').then((m) => ({ default: m.TrendsPage })))
const ProspectingResults = lazy(() => import('./pages/prospecting/insights/ResultsPage').then((m) => ({ default: m.ResultsPage })))
const ProspectingKeywords = lazy(() => import('./pages/prospecting/insights/KeywordsPage').then((m) => ({ default: m.KeywordsPage })))
const ProspectingXSearch = lazy(() => import('./pages/prospecting/insights/XSearchPage').then((m) => ({ default: m.XSearchPage })))
const ProspectingLinkedinSearch = lazy(() =>
  import('./pages/prospecting/insights/LinkedinSearchPage').then((m) => ({ default: m.LinkedinSearchPage })),
)
const ProspectingSettings = lazy(() =>
  import('./pages/prospecting/settings/ProspectingSettings').then((m) => ({ default: m.ProspectingSettings })),
)
const ProspectingHelperSetup = lazy(() =>
  import('./pages/prospecting/settings/HelperSetupPage').then((m) => ({ default: m.HelperSetupPage })),
)
// Operator (docs/SON-OF-BASEL-MERGE.md, Phase 3): what was the Son of Basel
// portal, part of this app since 2026-10-30. Each folder under pages/operator
// is one page group, by the file and named export in pages/operator/PORTING.md.
const OperatorLive = lazy(() => import('./pages/operator/live/LivePage').then((m) => ({ default: m.LivePage })))
const OperatorMeetings = lazy(() => import('./pages/operator/meetings/MeetingsPage').then((m) => ({ default: m.MeetingsPage })))
const OperatorMeeting = lazy(() =>
  import('./pages/operator/meetings/MeetingDetailPage').then((m) => ({ default: m.MeetingDetailPage })),
)
const OperatorTranscriptComparison = lazy(() =>
  import('./pages/operator/meetings/TranscriptComparisonPage').then((m) => ({ default: m.TranscriptComparisonPage })),
)
const OperatorShare = lazy(() => import('./pages/operator/share/SharePage').then((m) => ({ default: m.SharePage })))
const OperatorAsk = lazy(() => import('./pages/operator/ask/AskPage').then((m) => ({ default: m.AskPage })))
const OperatorInsights = lazy(() => import('./pages/operator/insights/InsightsPage').then((m) => ({ default: m.InsightsPage })))
const OperatorTalk = lazy(() => import('./pages/operator/talk/TalkPage').then((m) => ({ default: m.TalkPage })))
const OperatorDemo = lazy(() => import('./pages/operator/talk/DemoPage').then((m) => ({ default: m.DemoPage })))
const OperatorSignup = lazy(() => import('./pages/operator/signup/SignupPage').then((m) => ({ default: m.SignupPage })))
const OperatorClone = lazy(() => import('./pages/operator/clone/ClonePage').then((m) => ({ default: m.ClonePage })))
const OperatorMeetingSignin = lazy(() =>
  import('./pages/operator/clone/MeetingSigninPage').then((m) => ({ default: m.MeetingSigninPage })),
)
const OperatorOnboardingLayout = lazy(() =>
  import('./pages/operator/onboarding/OnboardingLayout').then((m) => ({ default: m.OnboardingLayout })),
)
const OperatorOnboardingIntro = lazy(() =>
  import('./pages/operator/onboarding/OnboardingIntro').then((m) => ({ default: m.OnboardingIntro })),
)
const OperatorOnboardingIdentity = lazy(() =>
  import('./pages/operator/onboarding/OnboardingIdentity').then((m) => ({ default: m.OnboardingIdentity })),
)
const OperatorOnboardingPersona = lazy(() =>
  import('./pages/operator/onboarding/OnboardingPersona').then((m) => ({ default: m.OnboardingPersona })),
)
const OperatorOnboardingPhoto = lazy(() =>
  import('./pages/operator/onboarding/OnboardingPhoto').then((m) => ({ default: m.OnboardingPhoto })),
)
const OperatorOnboardingVoice = lazy(() =>
  import('./pages/operator/onboarding/OnboardingVoice').then((m) => ({ default: m.OnboardingVoice })),
)
const OperatorOnboardingCalendar = lazy(() =>
  import('./pages/operator/onboarding/OnboardingCalendar').then((m) => ({ default: m.OnboardingCalendar })),
)
const OperatorPreferences = lazy(() =>
  import('./pages/operator/preferences/PreferencesPage').then((m) => ({ default: m.PreferencesPage })),
)
const OperatorSandbox = lazy(() => import('./pages/operator/sandbox/SandboxPage').then((m) => ({ default: m.SandboxPage })))
const OperatorSettings = lazy(() =>
  import('./pages/operator/settings/OperatorSettings').then((m) => ({ default: m.OperatorSettings })),
)
const OperatorAdmin = lazy(() => import('./pages/operator/settings/OperatorAdmin').then((m) => ({ default: m.OperatorAdmin })))
// Operator > Companies (docs/OPERATOR-HANDOVER.md): what to do today for every
// company, admin only. Not behind the Operator gate, which needs the meetings
// service: these pages only need an admin.
const OperatorCompaniesToday = lazy(() =>
  import('./pages/operator/companies/TodayPage').then((m) => ({ default: m.OperatorCompaniesTodayPage })),
)
const OperatorCompaniesList = lazy(() =>
  import('./pages/operator/companies/CompaniesPage').then((m) => ({ default: m.OperatorCompaniesListPage })),
)
const OperatorCompany = lazy(() => import('./pages/operator/companies/CompanyPage').then((m) => ({ default: m.OperatorCompanyPage })))
const OperatorPerson = lazy(() => import('./pages/operator/companies/PersonPage').then((m) => ({ default: m.OperatorPersonPage })))
const OperatorCompaniesSettings = lazy(() =>
  import('./pages/operator/companies/settings/OperatorCompaniesSettings').then((m) => ({ default: m.OperatorCompaniesSettings })),
)
// Discovery (docs/DISCOVERY.md): company lists, their signals and the scoring
// profiles, for every brand. The new list wizard is a takeover, below.
const DiscoveryLists = lazy(() => import('./pages/discovery/ListsPage').then((m) => ({ default: m.ListsPage })))
const DiscoveryList = lazy(() => import('./pages/discovery/ListPage').then((m) => ({ default: m.ListPage })))
const DiscoverySignals = lazy(() => import('./pages/discovery/SignalsPage').then((m) => ({ default: m.SignalsPage })))
const DiscoveryScoring = lazy(() => import('./pages/discovery/ScoringPage').then((m) => ({ default: m.ScoringPage })))
const DiscoveryNewList = lazy(() => import('./pages/discovery/NewListWizard').then((m) => ({ default: m.NewListWizard })))
const SettingsDiscovery = lazy(() => import('./pages/settings/DiscoverySettings'))
// AI Ark (docs/AI-ARK-HANDOVER.md): search AI Ark's people and companies and
// add the people to a list in Contacts. Admin only, inside Discovery.
const AiArkSearch = lazy(() => import('./pages/discovery/ai-ark/AiArkSearchPage').then((m) => ({ default: m.AiArkSearchPage })))
const AiArkExports = lazy(() => import('./pages/discovery/ai-ark/AiArkExportsPage').then((m) => ({ default: m.AiArkExportsPage })))
const SettingsAiArk = lazy(() =>
  import('./pages/discovery/ai-ark/settings/AiArkSettingsPage').then((m) => ({ default: m.AiArkSettingsPage })),
)
// Video Publication (0164): brand cards, a library, then the review page.
const Publish = lazy(() => import('./pages/publish/PublishPage').then((m) => ({ default: m.PublishPage })))
// The shell the thirteen render inside, and the list they render when nothing
// is chosen. Not lazy: the layout is on the critical path of every settings
// screen, so splitting it only buys a second spinner before the first one.
const SettingsOverview = lazy(() => import('./pages/settings/SettingsOverview'))

// Platform administration. Both read or write across brands, so both are behind
// `RequireAdmin` here as well as on the server.
const AdminBrands = lazy(() => import('./pages/admin/MasterViewPage').then((m) => ({ default: m.MasterViewPage })))
const AdminCampaigns = lazy(() => import('./pages/admin/AllCampaignsPage'))
const AdminProspects = lazy(() => import('./pages/admin/AllProspectsPage'))
const AdminPeople = lazy(() => import('./pages/admin/PeoplePage').then((m) => ({ default: m.PeoplePage })))

/**
 * `handle` carries the tab title and a human name for the screen. Anything that
 * needs the current page's identity reads it with `useMatches()` rather than
 * parsing the path.
 */
export interface RouteHandle {
  title: string
  /** The screen runs to the edges of the pane from 1024px, with no shell padding. */
  fullBleed?: boolean
}

function handle(title: string, options: Omit<RouteHandle, 'title'> = {}): RouteHandle {
  return { title, ...options }
}

const appRoutes: RouteObject[] = [
  // Signing in lands on the inbox, as Salesforge lands on Primebox. The
  // dashboard is gone and its address goes to the same place.
  { index: true, element: <Navigate to="/inbox" replace /> },
  { path: 'dashboard', element: <Navigate to="/inbox" replace /> },

  { path: 'prospects', element: <Prospects />, handle: handle('Contacts', { fullBleed: true }) },
  { path: 'prospects/lists', element: <ProspectLists />, handle: handle('Lists', { fullBleed: true }) },
  // Before `:id`, or the router reads `review` as a prospect id and answers 404.
  { path: 'prospects/review', element: <ReviewNames />, handle: handle('Check the names', { fullBleed: true }) },
  // Same reason, same placement: `luma` is a screen, not a prospect nobody can find.
  { path: 'prospects/luma', element: <LumaEvents />, handle: handle('Luma events', { fullBleed: true }) },
  // And the third one, ahead of `:id` for the third time for the same reason.
  {
    path: 'prospects/sales-navigator',
    element: <SalesNavigatorImport />,
    handle: handle('Sales Navigator import', { fullBleed: true }),
  },
  // The email queue that import fills, ahead of `:id` for the same reason.
  { path: 'prospects/enrichment', element: <EnrichmentQueue />, handle: handle('Email enrichment', { fullBleed: true }) },
  // Companies' social links waiting for a person (0149), ahead of `:id` the same way.
  { path: 'prospects/enrichment/links', element: <CheckLinks />, handle: handle('Check links', { fullBleed: true }) },
  // The personal profiles the search found (0144), ahead of `:id` the same way.
  { path: 'prospects/profile-search', element: <ProfileSearch />, handle: handle('Profile search', { fullBleed: true }) },
  { path: 'prospects/:id', element: <ProspectDetail />, handle: handle('Contact', { fullBleed: true }) },

  /*
   * ONE CAMPAIGN, ONE URL.
   *
   * There were two complete campaign trees here, and after the screens were
   * renamed both carried the SAME titles: `campaigns` and `playbooks` each said
   * Campaigns, each said Campaign. Five links from outside this feature pointed
   * at the retired one, so opening a campaign from a person's enrolment row gave
   * you a different screen from opening the same campaign in the sidebar, and
   * both screens called themselves the same thing.
   *
   * Worse, `/campaigns/new` matched `campaigns/:id` with the id "new", so the two
   * places offering to build a campaign were asking the detail page to load a
   * campaign that does not exist.
   *
   * Redirects rather than deletions, because these paths are in the handbook, in
   * people's bookmarks and in at least one runbook. The pages themselves are no
   * longer reachable from anywhere and can go in their own commit, which is a
   * deletion rather than a redirect and deserves to be reviewed as one.
   */
  { path: 'campaigns', element: <Navigate to="/playbooks" replace /> },
  { path: 'campaigns/new', element: <Navigate to="/playbooks/new" replace /> },
  { path: 'campaigns/:id', element: <RedirectToCampaign /> },

  // The merged feature. Building one (`new`) and editing one (`:playbookId/builder`)
  // are takeovers, declared further down. The campaign's own page is its
  // overview, and its old `?tab=` links to the editing sections redirect from
  // inside it to the builder tab that holds each one now.
  // Sequences, with Analytics and Workflows as its tabs, as Salesforge's
  // Sequences page has Analytics beside the list.
  { path: 'playbooks', element: <Playbooks />, handle: handle('Sequences', { fullBleed: true }) },
  { path: 'playbooks/analytics', element: <SequencesAnalytics />, handle: handle('Analytics', { fullBleed: true }) },
  { path: 'playbooks/workflows', element: <SequencesWorkflows />, handle: handle('Workflows', { fullBleed: true }) },
  { path: 'playbooks/:playbookId', element: <PlaybookDetail />, handle: handle('Sequence', { fullBleed: true }) },

  // The screen is called Workflows, but the path stays `methods` so links people
  // have already saved keep resolving.
  { path: 'methods', element: <Redirect to="/playbooks/workflows" /> },
  // The builder is for editing. A new sequence is made in the campaign wizard
  // or through the API, so the two old ways in to a blank builder and to the
  // workflow file upload land on the wizard instead. Redirects rather than
  // deletions, because the paths are in bookmarks. Ahead of `methods/:id`, a
  // takeover further down, so neither is read as a workflow id: a static
  // segment outranks a dynamic one wherever the two are declared.
  { path: 'methods/new', element: <Navigate to="/playbooks/new" replace /> },
  { path: 'methods/import', element: <Navigate to="/playbooks/new" replace /> },

  /*
   * DISCOVERY, the owner's module for finding which companies show money to
   * spend and a reason to buy, for any brand (2026-10-26, docs/DISCOVERY.md).
   * Its rows sit above Outreach in the sidebar.
   *
   * One pathless route holds the section behind `RequireAdmin`, because it can
   * start paid checks and is admin only for now. The API carries the real
   * check. Every screen is drawn in the Page frame, so each is full bleed, and
   * the table of one list is wide. `lists` itself is not a screen: Company
   * lists is the bare path, so a hand-typed `/discovery/lists` lands there.
   */
  {
    element: (
      <RequireAdmin fallback={<LoadingState />}>
        <Outlet />
      </RequireAdmin>
    ),
    children: [
      { path: 'discovery', element: <DiscoveryLists />, handle: handle('Company lists', { fullBleed: true }) },
      { path: 'discovery/lists', element: <Redirect to="/discovery" /> },
      { path: 'discovery/lists/:listId', element: <DiscoveryList />, handle: handle('Company list', { fullBleed: true }) },
      { path: 'discovery/signals', element: <DiscoverySignals />, handle: handle('Signals', { fullBleed: true }) },
      { path: 'discovery/scoring', element: <DiscoveryScoring />, handle: handle('Scoring', { fullBleed: true }) },
      // AI Ark is put away (owner, 2026-11-04): its pages stay in the code and a
      // bookmark lands on Discovery while the switch in @gojibasel/shared/ai-ark is on.
      { path: 'discovery/ai-ark', element: AI_ARK_PUT_AWAY ? <Navigate to="/discovery" replace /> : <AiArkSearch />, handle: handle('AI Ark', { fullBleed: true }) },
      { path: 'discovery/ai-ark/exports', element: AI_ARK_PUT_AWAY ? <Navigate to="/discovery" replace /> : <AiArkExports />, handle: handle('AI Ark exports', { fullBleed: true }) },
    ],
  },

  // There is no second screen for the sends that did not go out. A message that
  // was approved, was handed over and never arrived is a message that needs a
  // decision, and this is where a person looks at one message and decides what
  // happens to it. It comes back into this queue marked as not having sent, with
  // its own control, rather than to a surface of its own that nobody opens.
  { path: 'approvals', element: <Approvals />, handle: handle('Approvals', { fullBleed: true }) },
  { path: 'queue', element: <Queue />, handle: handle('Work queue', { fullBleed: true }) },

  // Video Publication (0164): the brand cards, one brand's library, and one
  // video. One page reads the address, and the query cache carries the brand
  // list from one screen to the next.
  { path: 'publish', element: <Publish />, handle: handle('Publish', { fullBleed: true }) },
  { path: 'publish/:brandId', element: <Publish />, handle: handle('Publish', { fullBleed: true }) },
  { path: 'publish/:brandId/videos/:videoId', element: <Publish />, handle: handle('Publish', { fullBleed: true }) },
  { path: 'publish/:brandId/posts/:postId', element: <Publish />, handle: handle('Publish', { fullBleed: true }) },

  /*
   * PROSPECTING, the prospects pages, drawn by this app since 2026-10-24
   * (docs/PROSPECTING-MERGE.md). They were another app's at /prospector,
   * with its own title, favicon and settings. Now they are routes of this
   * shell, so the title, the favicon, the sidebar and the workspace switcher
   * are this app's on every one of them.
   *
   * One layout route without a path holds them all: it asks the service who
   * the person is (an owner, a member with a seat, or nobody it knows) and
   * gives every page the brand filter. Each page is drawn in the Page frame,
   * so each is full bleed like the other screens drawn in it.
   *
   * The Review page is the Queue's One at a time mode, so its own address
   * lands there with the query it carried.
   */
  { path: 'prospecting/review', element: <Redirect to="/prospecting?mode=review" /> },
  {
    element: <ProspectingLayout />,
    children: [
      { path: 'prospecting', element: <ProspectingQueue />, handle: handle('Queue', { fullBleed: true }) },
      { path: 'prospecting/posts', element: <ProspectingPosts />, handle: handle('Posts', { fullBleed: true }) },
      { path: 'prospecting/creators', element: <ProspectingCreators />, handle: handle('Creators', { fullBleed: true }) },
      { path: 'prospecting/mentions', element: <ProspectingMentions />, handle: handle('Mentions', { fullBleed: true }) },
      { path: 'prospecting/competitors', element: <ProspectingCompetitors />, handle: handle('Competitors', { fullBleed: true }) },
      { path: 'prospecting/reviews', element: <ProspectingReviews />, handle: handle('Reviews', { fullBleed: true }) },
      { path: 'prospecting/prospects', element: <ProspectingProspects />, handle: handle('People found', { fullBleed: true }) },
      { path: 'prospecting/sequences', element: <ProspectingSequences />, handle: handle('Sequences, all brands', { fullBleed: true }) },
      { path: 'prospecting/trends', element: <ProspectingTrends />, handle: handle('Trends', { fullBleed: true }) },
      { path: 'prospecting/results', element: <ProspectingResults />, handle: handle('Results', { fullBleed: true }) },
      { path: 'prospecting/keywords', element: <ProspectingKeywords />, handle: handle('Keywords', { fullBleed: true }) },
      { path: 'prospecting/x-search', element: <ProspectingXSearch />, handle: handle('X search', { fullBleed: true }) },
      { path: 'prospecting/linkedin-search', element: <ProspectingLinkedinSearch />, handle: handle('LinkedIn search', { fullBleed: true }) },
    ],
  },
  /*
   * The old addresses, for a link inside the app that still names one. On a
   * page load the gateway answers these with a 308 before this app is asked
   * (lib/prospecting-gateway.ts), from the same table, so the two always
   * agree: @gojibasel/shared/prospecting-paths.
   */
  { path: 'prospector', element: <LegacyProspectingRedirect /> },
  { path: 'prospector/*', element: <LegacyProspectingRedirect /> },

  /*
   * OPERATOR, what was the Son of Basel portal (meetings, recordings, Ask,
   * insights, Talk, the clone), drawn by this app since 2026-10-30
   * (docs/SON-OF-BASEL-MERGE.md). Its backend stays a service on another box,
   * reached through /api/v1/operator.
   *
   * One layout route without a path holds them all, as Prospecting's does: it
   * asks the service who the person is (an admin, a member with a seat, or
   * nobody it knows) and sends a person whose clone is not set up yet to
   * onboarding. Each page is drawn in the Page frame, so each is full bleed.
   * The public share page is not here: it needs no sign-in, so it sits beside
   * the sign-in screen at the foot of this file.
   */
  { path: 'operator', element: <Navigate to="/operator/live" replace /> },
  /*
   * OPERATOR > COMPANIES: Today, every company, and one company's page. Admin
   * only, here and on the server, and outside the layout below, because that
   * gate asks the meetings service and these pages do not need it.
   */
  {
    element: (
      <RequireAdmin fallback={<LoadingState />}>
        <Outlet />
      </RequireAdmin>
    ),
    children: [
      { path: 'operator/companies', element: <OperatorCompaniesToday />, handle: handle('Companies', { fullBleed: true }) },
      { path: 'operator/companies/all', element: <OperatorCompaniesList />, handle: handle('All companies', { fullBleed: true }) },
      { path: 'operator/companies/:companyId', element: <OperatorCompany />, handle: handle('Company', { fullBleed: true }) },
      { path: 'operator/companies/people/:personId', element: <OperatorPerson />, handle: handle('Person', { fullBleed: true }) },
    ],
  },
  {
    element: <OperatorLayout />,
    children: [
      { path: 'operator/live', element: <OperatorLive />, handle: handle('Live', { fullBleed: true }) },
      { path: 'operator/meetings', element: <OperatorMeetings />, handle: handle('Meetings', { fullBleed: true }) },
      {
        // Operator admins only, which the page decides by the service's role (GET /me), as the sandbox does.
        path: 'operator/meetings/transcript-comparison',
        element: <OperatorTranscriptComparison />,
        handle: handle('Transcript comparison', { fullBleed: true }),
      },
      { path: 'operator/meetings/:id', element: <OperatorMeeting />, handle: handle('Meeting', { fullBleed: true }) },
      { path: 'operator/ask', element: <OperatorAsk />, handle: handle('Ask', { fullBleed: true }) },
      // One of the person's own Ask chats, so a reload or a shared link reopens it. The same element
      // as the new chat above, so moving from one to the other keeps the page (and a send in flight).
      { path: 'operator/ask/:conversationId', element: <OperatorAsk />, handle: handle('Ask', { fullBleed: true }) },
      { path: 'operator/insights', element: <OperatorInsights />, handle: handle('Insights', { fullBleed: true }) },
      { path: 'operator/talk', element: <OperatorTalk />, handle: handle('Talk', { fullBleed: true }) },
      { path: 'operator/clone', element: <OperatorClone />, handle: handle('Clone', { fullBleed: true }) },
      {
        path: 'operator/clone/meeting-signin',
        element: <OperatorMeetingSignin />,
        handle: handle('Meeting sign-in', { fullBleed: true }),
      },
      {
        // The steps render inside the onboarding layout's Outlet. The children
        // carry no fullBleed key, so this one decides for all of them.
        path: 'operator/onboarding',
        element: <OperatorOnboardingLayout />,
        handle: handle('Onboarding', { fullBleed: true }),
        children: [
          { index: true, element: <OperatorOnboardingIntro />, handle: handle('Onboarding') },
          { path: 'intro', element: <OperatorOnboardingIntro />, handle: handle('Onboarding') },
          { path: 'identity', element: <OperatorOnboardingIdentity />, handle: handle('Identity') },
          { path: 'persona', element: <OperatorOnboardingPersona />, handle: handle('Persona') },
          { path: 'photo', element: <OperatorOnboardingPhoto />, handle: handle('Photo') },
          { path: 'voice', element: <OperatorOnboardingVoice />, handle: handle('Voice') },
          { path: 'calendar', element: <OperatorOnboardingCalendar />, handle: handle('Calendar') },
        ],
      },
      { path: 'operator/preferences', element: <OperatorPreferences />, handle: handle('Preferences', { fullBleed: true }) },
      {
        // The sandbox and its showcase library, for Operator admins, as the
        // portal's middleware kept `/sandbox` to its own admins. The service's
        // role decides (GET /me), not this app's.
        path: 'operator/sandbox',
        element: (
          <RequireOperatorAdmin title="Sandbox">
            <OperatorSandbox />
          </RequireOperatorAdmin>
        ),
        handle: handle('Sandbox', { fullBleed: true }),
      },
    ],
  },
  /*
   * The portal's old addresses, for a link inside the app that still names
   * one: meeting links quoted in HANDOVER, in emails and in Drive. One table,
   * @gojibasel/shared/operator-paths, which the API reads for its 308s too.
   * `/share/<token>` and `/talk` (the public demo now) need no sign-in and
   * sit at the foot of this file, and `/signup` is a public page of its own. `/settings`, `/login` and the
   * rest of `/admin` stay this app's.
   */
  { path: 'meetings', element: <LegacyOperatorRedirect /> },
  { path: 'meetings/:id', element: <LegacyOperatorRedirect /> },
  { path: 'ask', element: <LegacyOperatorRedirect /> },
  { path: 'insights', element: <LegacyOperatorRedirect /> },
  { path: 'clone', element: <LegacyOperatorRedirect /> },
  { path: 'clone/meeting-signin', element: <LegacyOperatorRedirect /> },
  { path: 'onboarding', element: <LegacyOperatorRedirect /> },
  { path: 'onboarding/*', element: <LegacyOperatorRedirect /> },
  { path: 'sandbox', element: <LegacyOperatorRedirect /> },
  { path: 'preferences', element: <LegacyOperatorRedirect /> },
  { path: 'admin/users', element: <LegacyOperatorRedirect /> },
  { path: 'admin/credentials', element: <LegacyOperatorRedirect /> },

  // Full bleed: the label rail sits flush against the sidebar, the way the
  // reference's does, and the reader runs to the right edge.
  { path: 'inbox', element: <Inbox />, handle: handle('Inbox', { fullBleed: true }) },
  /*
   * THE VIEWS AS PATHS, AS LINKS INTO THE QUERY THE PAGE ALREADY READS.
   *
   * The inbox keeps its whole state in the query string: the label rail writes
   * `labelId` or `unlabelled`, together with every other filter, in one replace
   * that also drops the open thread. Moving the label into the path would make
   * the rail navigate instead, and split one filter bag across two places. So
   * these three paths are accepted and translated into the query the page
   * reads, which is the same list, and the query-string links keep working
   * untouched. Static segments, so they outrank `inbox/:id` below.
   */
  { path: 'inbox/all', element: <Redirect to="/inbox" drop={['labelId', 'unlabelled']} /> },
  { path: 'inbox/none', element: <Redirect to="/inbox?unlabelled=true" drop={['labelId']} /> },
  { path: 'inbox/label/:labelId', element: <Redirect to="/inbox?labelId=:labelId" drop={['unlabelled']} /> },
  /*
   * One thread by its id, the link the product has always shared. The thread
   * is the inbox with `?thread=` set, the same param every other way of
   * opening one writes, so this lands there with whatever filters the link
   * carried. Back to the list from it then never has to change path.
   */
  { path: 'inbox/:id', element: <Redirect to="/inbox?thread=:id" /> },

  { path: 'analytics', element: <Redirect to="/playbooks/analytics" /> },

  // The channel accounts screen, which has been drawn as the senders and
  // mailboxes screen since the Salesforge pass and now has the address to
  // match. `/settings/accounts` redirects here with its `?tab=`.
  { path: 'senders', element: <Senders />, handle: handle('Senders & Mailboxes', { fullBleed: true }) },
  { path: 'senders/mailboxes', element: <SendersMailboxes />, handle: handle('Mailboxes', { fullBleed: true }) },
  // A tab per brand: what each brand sends from, with the real address and
  // the connection, and its pins. A page since 2026-10-21, a drawer before.
  { path: 'senders/brands', element: <SendersBrands />, handle: handle('Brand senders', { fullBleed: true }) },
  { path: 'senders/brands/:brandId', element: <SendersBrands />, handle: handle('Brand senders', { fullBleed: true }) },
  // What each LinkedIn account's daily read of its own posts found, and the
  // people on them (0158). Open to everybody: the API answers for the active
  // brand only, so there is nothing here a member of the brand may not see.
  {
    path: 'senders/post-analytics',
    element: <SendersPostAnalytics />,
    handle: handle('Post analytics', { fullBleed: true }),
  },
  {
    // The phones are infrastructure, so they sit beside the mailboxes. Admin
    // only, as the device screen always was.
    path: 'senders/phones',
    element: (
      <RequireAdmin fallback={<LoadingState />}>
        <SendersPhones />
      </RequireAdmin>
    ),
    handle: handle('Phones', { fullBleed: true }),
  },

  {
    // Offers and the brand file, the two things a brand knows. The old paths,
    // `/settings/offers` and `/settings/knowledge`, redirect into the tabs.
    path: 'knowledge',
    element: <KnowledgeLayout />,
    // Drawn in the Page frame, so the shell adds no padding. The children
    // carry no fullBleed key, so this one decides for all of them.
    handle: handle('Knowledge', { fullBleed: true }),
    children: [
      { index: true, element: <Redirect to="/knowledge/offers" /> },
      { path: 'offers', element: <KnowledgeOffers />, handle: handle('Offers') },
      { path: 'files', element: <KnowledgeBrandFile />, handle: handle('Brand file') },
    ],
  },

  // The handbook belongs to nobody's settings, and the link at the foot of the
  // sidebar is where people find it. `/settings/handbook` redirects here.
  { path: 'handbook', element: <Handbook />, handle: handle('Handbook', { fullBleed: true }) },

  {
    /*
     * ONE DESTINATION, THIRTEEN SCREENS, AND THE SIDEBAR CARRIES ONE ROW.
     *
     * These were thirteen separate top-level routes and a "Setup" group in the
     * sidebar that had to fold, because configuration was more than half of the
     * whole navigation. They are children of one layout now. The paths are
     * unchanged, so every bookmark, runbook sentence and link in an older
     * message still resolves to the same screen, and what changed is that the
     * screen arrives with the other twelve listed beside it.
     *
     * The layout is NOT a gate. Three of these carry `RequireAdmin` exactly as
     * they did, and the API carries the real check under both.
     */
    path: 'settings',
    element: <SettingsLayout />,
    // One Page with Salesforge's tab strip for every settings screen, so the
    // shell adds no padding around any of them.
    handle: handle('Settings', { fullBleed: true }),
    children: [
      // The bare path used to redirect to Channel accounts, which answered a
      // question nobody asked. It lists what is here instead, with the sentence
      // each screen already carried in its tooltip.
      { index: true, element: <SettingsOverview />, handle: handle('Settings') },
      // Usage is the bare path. Its own name is accepted too, because that is
      // what the tab is called and what somebody will type.
      { path: 'usage', element: <Redirect to="/settings" /> },
      // Moved out of settings, each to a top-level home. Redirects rather than
      // deletions, because these paths are in bookmarks, runbooks and sent
      // messages, and each keeps its query so a `?tab=` still opens that tab.
      { path: 'accounts', element: <Redirect to="/senders" /> },
      { path: 'handbook', element: <Redirect to="/handbook" /> },
      {
        /*
         * ONE PAGE, AND NOT ADMIN-GATED AT THE ROUTE.
         *
         * It was two screens: `settings/my-limits` for what one person may send,
         * open to everybody, and `settings/policies` for what everybody may send,
         * admin only. Somebody looking for a number had to know which of the two
         * held it, and the answer depended on whether they were an admin.
         *
         * The gate moved inside. A member is offered the two tabs about their own
         * sending and is not shown the three they cannot change, because a control
         * the server would refuse reads as the product being broken. Editing a
         * ceiling is still admin only: `RequireAdmin` was never what enforced that,
         * the API was, and it still does.
         */
        path: 'safety',
        element: <SettingsSafetyControls />,
        handle: handle('Safety controls'),
      },
      // The two routes this replaced. Kept so a bookmark, a runbook and the
      // sentence under the sending-stopped banner all still land somewhere.
      { path: 'policies', element: <Navigate to="/settings/safety?tab=quota" replace /> },
      { path: 'my-limits', element: <Navigate to="/settings/safety?tab=mine" replace /> },
      { path: 'brands', element: <SettingsBrands />, handle: handle('Brands') },
      // One brand's prompts for writing content (owner, 2026-11-04), opened by clicking its row.
      { path: 'brands/:brandId', element: <SettingsBrandPrompts />, handle: handle('Brand prompts') },
      // The brand file has a tab of its own under Knowledge. Kept as a redirect
      // because this path is in older messages, in bookmarks, and in the
      // sentence a blocked launch prints.
      { path: 'knowledge', element: <Redirect to="/knowledge/files" /> },
      { path: 'offers', element: <Redirect to="/knowledge/offers" /> },
      { path: 'lead-magnets', element: <SettingsLeadMagnets />, handle: handle('Lead magnets') },
      { path: 'suppressions', element: <SettingsSuppressions />, handle: handle('Suppressions') },
      { path: 'activity', element: <SettingsActivity />, handle: handle('Activity log') },
      {
        // What is happening as it happens, and the buttons that block an
        // address. Admin only, here and on the server: it shows every brand's
        // changes and every phone, and a block turns people away at the door.
        path: 'activity/live',
        element: (
          <RequireAdmin fallback={<LoadingState />}>
            <SettingsLiveFeed />
          </RequireAdmin>
        ),
        handle: handle('Live feed'),
      },
      { path: 'variables', element: <SettingsVariables />, handle: handle('Variables') },
      {
        // Which Claude model each kind of call uses (0162). Admin only, here and
        // on the server, because the choice applies to every brand at once.
        path: 'ai-models',
        element: (
          <RequireAdmin fallback={<LoadingState />}>
            <SettingsAiModels />
          </RequireAdmin>
        ),
        handle: handle('AI models'),
      },
      { path: 'mailboxes', element: <Redirect to="/senders/mailboxes" /> },
      {
        // Minting and revoking engine credentials is admin only, like the endpoints.
        // The engine's tokens and reports are sections of Senders & Mailboxes >
        // Phones now, beside the phones they belong to.
        path: 'engine',
        element: <Redirect to="/senders/phones" />,
      },
      {
        // Reading the API reference is not an administrative act, so this is open to
        // anybody signed in. The keys panel inside it is the part that is admin only,
        // and it says so itself rather than hiding the whole page from a member who
        // has been handed a key and wants to know what it opens.
        path: 'api',
        element: <SettingsApi />,
        handle: handle('API reference'),
      },
      {
        // A person's own alert emails. Open to everybody signed in: the server
        // offers a member only the events of brands they may open, and an admin
        // the platform-wide ones as well.
        path: 'notifications',
        element: <SettingsNotifications />,
        handle: handle('Notifications'),
      },
      {
        // Registering a destination sends replies and prospect detail out of the
        // platform, and a test send puts a message in somebody's inbox, so this is
        // admin only on the server and admin only here.
        path: 'integrations',
        element: (
          <RequireAdmin fallback={<LoadingState />}>
            <SettingsIntegrations />
          </RequireAdmin>
        ),
        handle: handle('Alert destinations'),
      },
      {
        /*
         * PROSPECTING'S SETTINGS, A TAB OF THIS PAGE, DRAWN HERE.
         *
         * Until 2026-10-24 this tab was a doorway that loaded the old app's
         * own settings page, with its own copy of this tab strip, which drifted
         * from this one. Now it is this app's screen, inside this Settings page.
         *
         * Not admin only: an owner sees every setting there, and a member with
         * a seat sees their own (My voice, My feed, My brands). The layout asks
         * the service which, and a person with no seat gets one sentence
         * saying so. The browser helper's setup is a page under it.
         */
        path: 'prospecting',
        element: <ProspectingLayout frame="section" />,
        children: [
          { index: true, element: <ProspectingSettings />, handle: handle('Prospecting') },
          { path: 'helper', element: <ProspectingHelperSetup />, handle: handle('Browser helper') },
        ],
      },
      {
        /*
         * OPERATOR'S SETTINGS, A TAB OF THIS PAGE, behind the same gate as the
         * Operator pages, drawn as a section: the clone's agent, prompts,
         * models, voice and face, and its calendar. The portal's admin pages
         * (users, the credential pool, leads) are a page under it. Not admin
         * only at the route, as Prospecting's is not: the service decides
         * what each person may change there, and the layout asks it.
         */
        path: 'operator',
        element: <OperatorLayout frame="section" />,
        children: [
          { index: true, element: <OperatorSettings />, handle: handle('Operator') },
          { path: 'admin', element: <OperatorAdmin />, handle: handle('Operator admin') },
        ],
      },
      {
        // Operator > Companies: the company and people registry, schedule,
        // budget, sources and house rules. Admin only, here and on the server.
        path: 'operator-companies',
        element: (
          <RequireAdmin fallback={<LoadingState />}>
            <OperatorCompaniesSettings />
          </RequireAdmin>
        ),
        handle: handle('Operator companies'),
      },
      {
        // Discovery's sources, server keys, n8n webhooks and limits. Admin only,
        // here and on the server: the switches and limits cover every check.
        path: 'discovery',
        element: (
          <RequireAdmin fallback={<LoadingState />}>
            <SettingsDiscovery />
          </RequireAdmin>
        ),
        handle: handle('Discovery'),
      },
      {
        // AI Ark's settings row and spend. Admin only, here and on the server.
        path: 'ai-ark',
        element: AI_ARK_PUT_AWAY ? (
          <Navigate to="/settings" replace />
        ) : (
          <RequireAdmin fallback={<LoadingState />}>
            <SettingsAiArk />
          </RequireAdmin>
        ),
        handle: handle('AI Ark'),
      },
      // The doorway's old address, in bookmarks and in older messages.
      { path: 'prospector', element: <Redirect to="/settings/prospecting" /> },
      // The handset moved to Senders & Mailboxes, beside the accounts it carries.
      { path: 'device', element: <Redirect to="/senders/phones" /> },
      {
        /*
         * MOVED HERE FROM `/admin/people`, and it is the only path in this
         * change that moved.
         *
         * Who may sign in and what they may reach is configuration, not a
         * cross-brand report, so it belongs beside the other twelve rather than
         * with the three admin screens that read every brand at once. The old
         * path redirects, below, because it is in invitation emails.
         */
        path: 'people',
        element: (
          <RequireAdmin fallback={<LoadingState />}>
            <AdminPeople />
          </RequireAdmin>
        ),
        handle: handle('People'),
      },
    ],
  },

  {
    // Reads every brand at once, which is exactly why it is the super admin view.
    path: 'admin/brands',
    element: (
      <RequireSuperAdminView fallback={<LoadingState />}>
        <AdminBrands />
      </RequireSuperAdminView>
    ),
    handle: handle('All brands', { fullBleed: true }),
  },
  {
    // Every campaign in every brand, which is exactly why it is the super admin view.
    path: 'admin/campaigns',
    element: (
      <RequireSuperAdminView fallback={<LoadingState />}>
        <AdminCampaigns />
      </RequireSuperAdminView>
    ),
    handle: handle('All sequences', { fullBleed: true }),
  },
  {
    // Every person in every brand. Same widening, same guard.
    path: 'admin/prospects',
    element: (
      <RequireSuperAdminView fallback={<LoadingState />}>
        <AdminProspects />
      </RequireSuperAdminView>
    ),
    handle: handle('All people', { fullBleed: true }),
  },
  // People moved under settings, where the rest of the configuration is. This
  // path is in invitation emails that have already been sent, so it redirects
  // rather than 404s.
  { path: 'admin/people', element: <Navigate to="/settings/people" replace /> },

  { path: '*', element: <NotFound />, handle: handle('Page not found') },
]

export /**
 * The old campaign detail path, carrying its id across to the one that survived.
 *
 * A component rather than a static Navigate because the id is in the path and
 * `replace` matters: somebody following an old link should not have to press
 * back twice to leave the screen they never asked for.
 */
function RedirectToCampaign() {
  const { id } = useParams()
  return <Navigate to={id === undefined ? '/playbooks' : `/playbooks/${id}`} replace />
}

/**
 * The screens that take the whole window, with no sidebar.
 *
 * A route moves in by being added here and taken out of `appRoutes`, in the
 * change that builds it. The editors live here: building a campaign, editing
 * one, and editing a workflow, so a campaign and a workflow are drawn on the
 * same canvas in the same frame.
 *
 * The builder's tabs are one route with an optional segment rather than six
 * routes, on purpose. One route element serves every tab, so moving between
 * tabs keeps the page mounted, and with it a drawing nobody has saved yet.
 * A segment that names no tab is sent to the first one by the page itself.
 */
const takeoverRoutes: RouteObject[] = [
  { path: 'playbooks/new', element: <PlaybookNew />, handle: handle('New sequence') },
  { path: 'playbooks/:playbookId/builder/:tab?', element: <PlaybookBuilder />, handle: handle('Sequence builder') },
  // The CSV import is Salesforge's full-screen upload wizard. A static path,
  // so it outranks the shell's `prospects/:id` declared after it.
  { path: 'prospects/import', element: <ProspectImport />, handle: handle('Import contacts') },
  { path: 'methods/:id', element: <MethodDetail />, handle: handle('Workflow') },
  // Making a Discovery company list: the source, the file and its columns, the
  // name and profile, then the import. Admin only, like the rest of Discovery.
  {
    path: 'discovery/new',
    element: (
      <RequireAdmin fallback={<FullPageLoading />}>
        <DiscoveryNewList />
      </RequireAdmin>
    ),
    handle: handle('New company list'),
  },
]

export const routes: RouteObject[] = [
  { path: '/login', element: <Login /> },
  /*
   * PUBLIC, NO SIGN-IN: a meeting somebody shared, opened from the link they
   * were sent. Outside `RequireAuth`, outside the shell and outside the
   * Operator gate, like the sign-in screen beside it. Lazy, so it has its own
   * Suspense here, where no shell is around it to hold one.
   */
  {
    path: '/operator/share/:token',
    element: (
      <Suspense fallback={<PublicPageLoading />}>
        <OperatorShare />
      </Suspense>
    ),
    handle: handle('Shared meeting'),
  },
  /*
   * PUBLIC, NO SIGN-IN: Talk's demo, the power admin's clone on a video call
   * with a guest, while the power admin's timed switch is open. Built like the
   * share page beside it: outside `RequireAuth`, the shell and the Operator
   * gate. It reads GET /talk/access, and while that is closed it says so in a
   * sentence and points a team member at Talk inside the app.
   */
  {
    path: '/operator/demo',
    element: (
      <Suspense fallback={<PublicPageLoading />}>
        <OperatorDemo />
      </Suspense>
    ),
    handle: handle('Talk demo'),
  },
  // The portal's share links and its Talk demo, sent on before anybody is asked to sign in.
  { path: '/share/:token', element: <LegacyOperatorRedirect /> },
  { path: '/talk', element: <LegacyOperatorRedirect /> },
  /*
   * PUBLIC, NO SIGN-IN: the request form ("Tell us what you want to build"),
   * the portal's `/signup`, back on 2026-10-30. Its one request is the
   * gateway's anonymous path; the lead lands in Settings > Operator > Admin >
   * Leads and in Basel's inbox. A signed-in person is sent to Operator.
   */
  {
    path: '/signup',
    element: (
      <Suspense fallback={<PublicPageLoading />}>
        <OperatorSignup />
      </Suspense>
    ),
    handle: handle('Tell us what you want to build'),
  },
  {
    element: (
      <RequireAuth fallback={<FullPageLoading />}>
        <TakeoverLayout />
      </RequireAuth>
    ),
    children: takeoverRoutes,
  },
  {
    path: '/',
    element: (
      <RequireAuth fallback={<FullPageLoading />}>
        <AppShell />
      </RequireAuth>
    ),
    children: appRoutes,
  },
]

/** The wait for a public page, which has no session to check. */
function PublicPageLoading() {
  return (
    <div className="min-h-screen bg-canvas">
      <LoadingState label="Opening the page" />
    </div>
  )
}

function FullPageLoading() {
  return (
    <div className="min-h-screen bg-canvas">
      <LoadingState label="Checking your session" />
    </div>
  )
}

/**
 * The router, built when the app starts rather than when this file is read.
 *
 * A browser router reads `window` the moment it is made, so building it at
 * import time meant nothing could import this table without a browser. Tests
 * now match real paths against `routes` itself, which is the only honest way
 * to say a redirect lands somewhere.
 */
export function createAppRouter() {
  return createBrowserRouter(routes, {
    // Opt in early to the router behaviour that becomes the default in the next
    // major, so the console is not carrying a migration later.
    future: { v7_relativeSplatPath: true, v7_fetcherPersist: true, v7_normalizeFormMethod: true },
  })
}
